import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Group, Paper, Select, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { IconChartBar } from '@/components/icons'
import { api, ApiError } from '@/lib/api'
import type { ScheduleStaffingAnalysisRow, ScheduleStaffingSectionKey } from '@/types/api'
import { SCHEDULE_TZ_MSK } from '@/pages/schedules/schedule-utils'

const SECTION_ITEMS: { key: ScheduleStaffingSectionKey; label: string }[] = [
  { key: 'overview', label: 'Краткий вывод' },
  { key: 'trend', label: 'Тренд и прогноз' },
  { key: 'hourly', label: 'Покрытие и рекомендации по часам' },
  { key: 'operators', label: 'Операторы и проекты' },
  { key: 'steps', label: 'Практические шаги' },
]

type SectionUiStatus = 'pending' | 'running' | 'success' | 'error'

function statusLabel(status?: string): string {
  if (status === 'running') return 'генерируется'
  if (status === 'success') return 'готово'
  if (status === 'error') return 'ошибка'
  return 'ожидает'
}

function statusColor(status?: string): string {
  if (status === 'running') return 'blue'
  if (status === 'success') return 'green'
  if (status === 'error') return 'red'
  return 'gray'
}

function normalizeSectionStatuses(
  statuses?: Record<string, string> | null,
): Record<ScheduleStaffingSectionKey, SectionUiStatus> {
  const out = {} as Record<ScheduleStaffingSectionKey, SectionUiStatus>
  for (const item of SECTION_ITEMS) {
    const status = statuses?.[item.key]
    out[item.key] = status === 'success' ? 'success' : 'pending'
  }
  return out
}

function ymOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < 14; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    const v = `${y}-${String(m).padStart(2, '0')}`
    const label = `${String(m).padStart(2, '0')}.${y}`
    out.push({ value: v, label })
  }
  return out
}

type Props = {
  /** Увеличьте, чтобы перезагрузить сохранённый анализ (кнопка «Обновить» в шапке табеля). */
  refreshTick?: number
}

export function ScheduleStaffingAnalysisPanel({ refreshTick = 0 }: Props) {
  const opts = useMemo(() => ymOptions(), [])
  const [yearMonth, setYearMonth] = useState(() => opts[0]?.value ?? '')
  const [row, setRow] = useState<ScheduleStaffingAnalysisRow | null>(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatingSection, setGeneratingSection] = useState<ScheduleStaffingSectionKey | null>(null)
  const [sectionStatuses, setSectionStatuses] = useState<Record<ScheduleStaffingSectionKey, SectionUiStatus>>(
    () => normalizeSectionStatuses(),
  )
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<ScheduleStaffingSectionKey, string>>>({})

  const loadLatest = useCallback(async () => {
    if (!yearMonth) return
    setLoading(true)
    try {
      const r = await api.scheduleStaffingAnalysisLatest(yearMonth)
      setRow(r)
      setSectionStatuses(normalizeSectionStatuses(r?.section_statuses))
      setSectionErrors({})
    } catch {
      setRow(null)
      setSectionStatuses(normalizeSectionStatuses())
      setSectionErrors({})
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить анализ', color: 'red' })
    } finally {
      setLoading(false)
    }
  }, [yearMonth])

  useEffect(() => {
    void loadLatest()
  }, [loadLatest, refreshTick])

  const onGenerate = async () => {
    if (!yearMonth) return
    setGenerating(true)
    setSectionErrors({})
    let hasErrors = false
    try {
      for (const item of SECTION_ITEMS) {
        setGeneratingSection(item.key)
        setSectionStatuses((prev) => ({ ...prev, [item.key]: 'running' }))
        try {
          const result = await api.scheduleStaffingAnalysisGenerateSection({
            year_month: yearMonth,
            section: item.key,
          })
          setRow(result.row)
          setSectionStatuses((prev) => ({ ...prev, [item.key]: 'success' }))
          setSectionErrors((prev) => {
            const next = { ...prev }
            delete next[item.key]
            return next
          })
        } catch (e) {
          hasErrors = true
          setSectionStatuses((prev) => ({ ...prev, [item.key]: 'error' }))
          setSectionErrors((prev) => ({
            ...prev,
            [item.key]: e instanceof ApiError ? e.message : 'Не удалось сгенерировать блок',
          }))
        }
      }
      showNotification({
        title: hasErrors ? 'Готово частично' : 'Готово',
        message: hasErrors ? 'Часть блоков не сгенерировалась, их можно перезапустить отдельно' : 'Анализ обновлён',
        color: hasErrors ? 'yellow' : 'green',
      })
    } finally {
      setGeneratingSection(null)
      setGenerating(false)
    }
  }

  const onGenerateSection = async (section: ScheduleStaffingSectionKey) => {
    if (!yearMonth || generating) return
    setGenerating(true)
    setGeneratingSection(section)
    setSectionStatuses((prev) => ({ ...prev, [section]: 'running' }))
    setSectionErrors((prev) => {
      const next = { ...prev }
      delete next[section]
      return next
    })
    try {
      const result = await api.scheduleStaffingAnalysisGenerateSection({ year_month: yearMonth, section })
      setRow(result.row)
      setSectionStatuses((prev) => ({ ...prev, [section]: 'success' }))
      showNotification({ title: 'Готово', message: 'Блок отчёта обновлён', color: 'green' })
    } catch (e) {
      setSectionStatuses((prev) => ({ ...prev, [section]: 'error' }))
      const message = e instanceof ApiError ? e.message : 'Не удалось сгенерировать блок'
      setSectionErrors((prev) => ({ ...prev, [section]: message }))
      showNotification({
        title: 'Ошибка',
        message,
        color: 'red',
      })
    } finally {
      setGeneratingSection(null)
      setGenerating(false)
    }
  }

  return (
    <Stack gap="md" px="md" pb="md" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Group gap="sm" align="center">
        <IconChartBar size={22} stroke={1.5} />
        <Text fw={700} size="lg">
          Анализ загрузки табеля (AI)
        </Text>
      </Group>
      <Text size="sm" c="dimmed">
        Сопоставление потока обращений и планового графика за месяц, рекомендации по часам и по сотрудникам/проектам.
        Данные: новые обращения по часам и показатели операторов за выбранный месяц, план — средняя численность по графику.
      </Text>

      <Card withBorder padding="md" radius="md">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <Select
            label="Месяц"
            data={opts}
            value={yearMonth}
            onChange={(v) => setYearMonth(v || '')}
            maw={220}
          />
          <Button loading={generating} onClick={() => void onGenerate()}>
            Сгенерировать весь отчёт
          </Button>
          <Button variant="default" loading={loading} onClick={() => void loadLatest()}>
            Обновить из сохранённого
          </Button>
        </Group>
      </Card>

      {generating ? (
        <Alert color="blue" title="Генерация анализа">
          {generatingSection
            ? `Сейчас генерируется блок: ${SECTION_ITEMS.find((item) => item.key === generatingSection)?.label ?? generatingSection}.`
            : 'Генерация отчёта запущена.'}
        </Alert>
      ) : null}

      <Card withBorder padding="md" radius="md">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Блоки отчёта
          </Text>
          {SECTION_ITEMS.map((item) => {
            const status = sectionStatuses[item.key]
            const error = sectionErrors[item.key]
            return (
              <Group key={item.key} justify="space-between" align="center" gap="sm" wrap="wrap">
                <Group gap="xs">
                  <Text size="sm">{item.label}</Text>
                  <Badge color={statusColor(status)} variant="light">
                    {statusLabel(status)}
                  </Badge>
                  {error ? (
                    <Text size="xs" c="red">
                      {error}
                    </Text>
                  ) : null}
                </Group>
                <Button
                  size="xs"
                  variant="default"
                  loading={generatingSection === item.key}
                  disabled={generating && generatingSection !== item.key}
                  onClick={() => void onGenerateSection(item.key)}
                >
                  {status === 'success' ? 'Перегенерировать' : 'Сгенерировать'}
                </Button>
              </Group>
            )
          })}
        </Stack>
      </Card>

      {row?.created_at ? (
        <Text size="xs" c="dimmed">
          Сохранено:{' '}
          {new Intl.DateTimeFormat('ru-RU', {
            timeZone: SCHEDULE_TZ_MSK,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(row.created_at))}
          {row.provider_used ? ` · ${row.provider_used}` : ''}
          {row.model_used ? ` · ${row.model_used}` : ''}
        </Text>
      ) : null}

      {!loading && !row?.content_markdown ? (
        <Alert color="gray" title="Нет сохранённого анализа">
          Выберите месяц и нажмите «Сгенерировать». Нужны настроенные ключи AI в разделе «Настройки рабочего пространства».
        </Alert>
      ) : null}

      {row?.content_markdown ? (
        <Paper withBorder p="md" radius="md">
          <style>{`
            .schedule-staffing-md {
              font-size: var(--mantine-font-size-sm);
              line-height: 1.55;
              color: var(--mantine-color-text);
            }
            .schedule-staffing-md h1 {
              font-size: 1.2rem;
              font-weight: 700;
              margin: 0 0 0.5rem;
            }
            .schedule-staffing-md h2 {
              font-size: 1.05rem;
              font-weight: 700;
              margin: 1rem 0 0.5rem;
            }
            .schedule-staffing-md h3 {
              font-size: 1rem;
              font-weight: 600;
              margin: 0.75rem 0 0.35rem;
            }
            .schedule-staffing-md p {
              margin: 0 0 0.5rem;
            }
            .schedule-staffing-md ul,
            .schedule-staffing-md ol {
              margin: 0 0 0.5rem;
              padding-left: 1.25rem;
            }
            .schedule-staffing-md code {
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
              font-size: 0.92em;
            }
            .schedule-staffing-md pre {
              margin: 0 0 0.5rem;
              padding: 0.5rem 0.65rem;
              border-radius: var(--mantine-radius-sm);
              background: var(--mantine-color-gray-1);
              overflow: auto;
            }
            .schedule-staffing-md pre code {
              font-size: inherit;
            }
            .schedule-staffing-md table {
              border-collapse: collapse;
              width: 100%;
              margin: 0 0 0.5rem;
              font-size: 0.92em;
            }
            .schedule-staffing-md th,
            .schedule-staffing-md td {
              border: 1px solid var(--mantine-color-gray-4);
              padding: 0.35rem 0.5rem;
            }
          `}</style>
          <div className="schedule-staffing-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{row.content_markdown}</ReactMarkdown>
          </div>
        </Paper>
      ) : null}
    </Stack>
  )
}
